import { AppState, Account, RecurringPayment, OneOffPayment, Pot, PaydayRule, CreditCard } from '../types';

const STORAGE_KEY = 'pennies-app-data';

const defaultState: AppState = {
  account: {
    currentBalance: 0,
    lastUpdated: new Date()
  },
  recurringPayments: [],
  oneOffPayments: [],
  pots: [],
  paydayRules: [],
  creditCards: []
};

export const loadAppState = (): AppState => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return defaultState;
    }

    const parsed = JSON.parse(stored);
    
    return {
      ...parsed,
      account: {
        ...parsed.account,
        lastUpdated: (() => {
          if (!parsed.account.lastUpdated) return new Date();
          const date = new Date(parsed.account.lastUpdated);
          return isNaN(date.getTime()) ? new Date() : date;
        })()
      },
      recurringPayments: (parsed.recurringPayments || []).map((payment: any) => ({
        ...payment,
        startDate: payment.startDate ? new Date(payment.startDate) : undefined,
        endDate: payment.endDate ? new Date(payment.endDate) : undefined
      })),
      oneOffPayments: parsed.oneOffPayments.map((payment: any) => ({
        ...payment,
        date: new Date(payment.date)
      })),
      pots: parsed.pots || [],
      paydayRules: (parsed.paydayRules || []).map((rule: any) => ({
        ...rule,
        startDate: rule.startDate ? new Date(rule.startDate) : undefined,
        endDate: rule.endDate ? new Date(rule.endDate) : undefined
      })),
      creditCards: parsed.creditCards || []
    };
  } catch (error) {
    console.error('Failed to load app state from localStorage:', error);
    return defaultState;
  }
};

export const saveAppState = (state: AppState): void => {
  try {
    const serialized = JSON.stringify(state);
    localStorage.setItem(STORAGE_KEY, serialized);
  } catch (error) {
    console.error('Failed to save app state to localStorage:', error);
  }
};

export const updateAccount = (account: Account): void => {
  const state = loadAppState();
  state.account = { ...account, lastUpdated: new Date() };
  saveAppState(state);
};

export const addRecurringPayment = (payment: Omit<RecurringPayment, 'id'>): void => {
  const state = loadAppState();
  const newPayment: RecurringPayment = {
    ...payment,
    id: generateId()
  };
  state.recurringPayments.push(newPayment);
  saveAppState(state);
};

export const updateRecurringPayment = (id: string, payment: Partial<RecurringPayment>): void => {
  const state = loadAppState();
  const index = state.recurringPayments.findIndex(p => p.id === id);
  if (index !== -1) {
    state.recurringPayments[index] = { ...state.recurringPayments[index], ...payment };
    saveAppState(state);
  }
};

export const deleteRecurringPayment = (id: string): void => {
  const state = loadAppState();
  state.recurringPayments = state.recurringPayments.filter(p => p.id !== id);
  saveAppState(state);
};

export const addOneOffPayment = (payment: Omit<OneOffPayment, 'id'>): void => {
  const state = loadAppState();
  const newPayment: OneOffPayment = {
    ...payment,
    id: generateId()
  };
  state.oneOffPayments.push(newPayment);
  saveAppState(state);
};

export const updateOneOffPayment = (id: string, payment: Partial<OneOffPayment>): void => {
  const state = loadAppState();
  const index = state.oneOffPayments.findIndex(p => p.id === id);
  if (index !== -1) {
    state.oneOffPayments[index] = { ...state.oneOffPayments[index], ...payment };
    saveAppState(state);
  }
};

export const deleteOneOffPayment = (id: string): void => {
  const state = loadAppState();
  state.oneOffPayments = state.oneOffPayments.filter(p => p.id !== id);
  saveAppState(state);
};

export const addPot = (pot: Omit<Pot, 'id'>): void => {
  const state = loadAppState();
  const newPot: Pot = {
    ...pot,
    id: generateId()
  };
  state.pots.push(newPot);
  saveAppState(state);
};

export const updatePot = (id: string, pot: Partial<Pot>): void => {
  const state = loadAppState();
  const index = state.pots.findIndex(p => p.id === id);
  if (index !== -1) {
    state.pots[index] = { ...state.pots[index], ...pot };
    saveAppState(state);
  }
};

export const deletePot = (id: string): void => {
  const state = loadAppState();
  state.pots = state.pots.filter(p => p.id !== id);
  saveAppState(state);
};

export const addPaydayRule = (rule: Omit<PaydayRule, 'id'>): void => {
  const state = loadAppState();
  const newRule: PaydayRule = {
    ...rule,
    id: generateId()
  };
  state.paydayRules.push(newRule);
  saveAppState(state);
};

export const updatePaydayRule = (id: string, rule: Partial<PaydayRule>): void => {
  const state = loadAppState();
  const index = state.paydayRules.findIndex(r => r.id === id);
  if (index !== -1) {
    state.paydayRules[index] = { ...state.paydayRules[index], ...rule };
    saveAppState(state);
  }
};

export const deletePaydayRule = (id: string): void => {
  const state = loadAppState();
  state.paydayRules = state.paydayRules.filter(r => r.id !== id);
  saveAppState(state);
};

const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

export const exportData = (): string => {
  const state = loadAppState();
  return JSON.stringify(state, null, 2);
};

export const importData = (jsonData: string): boolean => {
  try {
    const data = JSON.parse(jsonData);

    const validatedData: AppState = {
      account: data.account || defaultState.account,
      recurringPayments: (data.recurringPayments || []).map((payment: any) => ({
        ...payment,
        startDate: payment.startDate ? new Date(payment.startDate) : undefined,
        endDate: payment.endDate ? new Date(payment.endDate) : undefined
      })),
      oneOffPayments: (data.oneOffPayments || []).map((payment: any) => ({
        ...payment,
        date: new Date(payment.date)
      })),
      pots: data.pots || [],
      paydayRules: (data.paydayRules || []).map((rule: any) => ({
        ...rule,
        startDate: rule.startDate ? new Date(rule.startDate) : undefined,
        endDate: rule.endDate ? new Date(rule.endDate) : undefined
      })),
      creditCards: data.creditCards || []
    };

    saveAppState(validatedData);
    return true;
  } catch (error) {
    console.error('Failed to import data:', error);
    return false;
  }
};

